import React, { useEffect, useState } from "react";
import { AudienceSegment } from "@/features/orders-analytics/api/orders-analytics.api";
import {
    AudienceSegmentType,
    OrdersAnalyticsFilters,
} from "@/features/orders-analytics/types/orders-analytics.types";
import "@/features/orders-analytics/styles/audience-export.css";

type Props = {
    baseFilters: OrdersAnalyticsFilters;
    workspaces: string[];
    rfmSegments: string[];
    onExport: (filters: OrdersAnalyticsFilters, segment: AudienceSegment) => void;
    onSave: (fileName: string) => void;
    onClear: () => void;
    ready: boolean;
    loading: boolean;
    error: string | null;
    count: number | null;
};

const SEGMENT_TABS: { type: AudienceSegmentType; label: string }[] = [
    { type: "none", label: "All in scope" },
    { type: "rfm", label: "RFM segment" },
    { type: "lifecycle", label: "Lifecycle" },
];

const LIFECYCLE_OPTIONS: { value: string; label: string }[] = [
    { value: "active", label: "Active" },
    { value: "at_risk", label: "At risk" },
    { value: "dormant", label: "Dormant" },
];

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

const todayStamp = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const slug = (value: string): string =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// First 3 alphanumerics of a workspace, to tag the file with its origin.
const workspaceCode = (workspace: string): string =>
    workspace.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 3);

const MetaGlyph: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M7 7c-2.8 0-5 2.2-5 5s2.2 5 5 5c2 0 3.4-1.2 4.5-3 .3-.5.6-1 1-1.6.4.6.7 1.1 1 1.6C14.6 15.8 16 17 18 17c2.8 0 5-2.2 5-5s-2.2-5-5-5c-2 0-3.4 1.2-4.5 3-.3.5-.6 1-1 1.6-.4-.6-.7-1.1-1-1.6C10.4 8.2 9 7 7 7zm0 2c1 0 1.9.8 2.8 2.2L8.5 13.4C7.9 14.3 7.5 15 7 15c-1.7 0-3-1.3-3-3s1.3-3 3-3zm10 0c1.7 0 3 1.3 3 3s-1.3 3-3 3c-.5 0-.9-.7-1.5-1.6l-1.3-2.2C15.1 9.8 16 9 17 9z" />
    </svg>
);

const CheckMark: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 13l4 4L19 7" />
    </svg>
);

export const AudienceExportSection: React.FC<Props> = ({
    baseFilters,
    workspaces,
    rfmSegments,
    onExport,
    onSave,
    onClear,
    ready,
    loading,
    error,
    count,
}) => {
    const [workspaceMode, setWorkspaceMode] = useState<"all" | "some">("all");
    const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
    const [segmentType, setSegmentType] = useState<AudienceSegmentType>("none");
    const [rfmValue, setRfmValue] = useState("");
    const [lifecycleValue, setLifecycleValue] = useState("active");
    const [fileName, setFileName] = useState("");
    const [fileNameEdited, setFileNameEdited] = useState(false);
    const [progress, setProgress] = useState(0);

    const segmentValue =
        segmentType === "rfm" ? rfmValue : segmentType === "lifecycle" ? lifecycleValue : "";
    const effectiveWorkspaces = workspaceMode === "all" ? [] : selectedWorkspaces;

    const scopeLabel =
        workspaceMode === "all"
            ? "All workspaces"
            : selectedWorkspaces.length === 1
              ? selectedWorkspaces[0]
              : `${selectedWorkspaces.length} workspaces`;
    const segmentLabel =
        segmentType === "rfm"
            ? `RFM · ${rfmValue}`
            : segmentType === "lifecycle"
              ? `Lifecycle · ${LIFECYCLE_OPTIONS.find((o) => o.value === lifecycleValue)?.label ?? lifecycleValue}`
              : "All accounts";

    const defaultFileName = [
        "meta-audience",
        effectiveWorkspaces.length === 1 ? workspaceCode(effectiveWorkspaces[0]) : "",
        segmentType !== "none" && segmentValue !== "" ? slug(segmentValue) : "",
        todayStamp(),
    ]
        .filter((part) => part !== "")
        .join("-")
        .concat(".csv");

    useEffect(() => {
        if (!fileNameEdited) {
            setFileName(defaultFileName);
        }
    }, [defaultFileName, fileNameEdited]);

    const generateDisabled =
        loading ||
        (segmentType === "rfm" && rfmValue === "") ||
        (workspaceMode === "some" && selectedWorkspaces.length === 0);

    // No streaming progress from a single blob request, so ease toward 92% while
    // it's in flight and snap to 100% when it resolves.
    useEffect(() => {
        if (!loading) {
            return;
        }
        setProgress(8);
        const id = setInterval(() => {
            setProgress((p) => (p < 92 ? p + Math.max(1, Math.round((92 - p) * 0.12)) : p));
        }, 220);
        return () => clearInterval(id);
    }, [loading]);

    useEffect(() => {
        if (ready) {
            setProgress(100);
        }
    }, [ready]);

    // Any scope change invalidates a prepared audience so a stale file can't be
    // saved; the user regenerates with the new selection.
    const invalidate = () => {
        if (ready) {
            onClear();
        }
    };

    const setWorkspaceModeSafe = (mode: "all" | "some") => {
        setWorkspaceMode(mode);
        invalidate();
    };

    const toggleWorkspace = (workspace: string) => {
        setSelectedWorkspaces((current) =>
            current.includes(workspace)
                ? current.filter((w) => w !== workspace)
                : [...current, workspace]
        );
        invalidate();
    };

    const setSegmentTypeSafe = (type: AudienceSegmentType) => {
        setSegmentType(type);
        invalidate();
    };

    const segButton = (
        active: boolean,
        label: string,
        onClick: () => void,
        key?: string
    ): React.ReactElement => (
        <button
            key={key}
            type="button"
            aria-pressed={active}
            onClick={onClick}
            className={active ? "ae-seg__btn is-active" : "ae-seg__btn"}
        >
            {label}
        </button>
    );

    return (
        <section className="section">
            <div className="ae-panel">
                <div className="ae-panel__hd">
                    <span className="ae-panel__title">Audience Export</span>
                    <span className="ae-meta-badge">
                        <MetaGlyph />
                        Meta Custom Audiences
                    </span>
                </div>

                <div className="ae-panel__body">
                    <div className="ae-field">
                        <span className="ae-field__lbl">Workspaces</span>
                        <div className="ae-seg" role="group" aria-label="Workspace scope">
                            {segButton(workspaceMode === "all", "All workspaces", () =>
                                setWorkspaceModeSafe("all")
                            )}
                            {segButton(workspaceMode === "some", "Select workspaces", () =>
                                setWorkspaceModeSafe("some")
                            )}
                        </div>
                    </div>

                    {workspaceMode === "some" ? (
                        <div className="ae-picker" role="group" aria-label="Workspaces">
                            {workspaces.length === 0 ? (
                                <span className="ae-picker__empty">No workspaces in range.</span>
                            ) : (
                                workspaces.map((workspace) => (
                                    <label key={workspace} className="ae-chk">
                                        <input
                                            type="checkbox"
                                            checked={selectedWorkspaces.includes(workspace)}
                                            onChange={() => toggleWorkspace(workspace)}
                                        />
                                        <span>{workspace}</span>
                                    </label>
                                ))
                            )}
                        </div>
                    ) : null}

                    <div className="ae-field">
                        <span className="ae-field__lbl">Segment</span>
                        <div className="ae-seg" role="group" aria-label="Audience segment">
                            {SEGMENT_TABS.map((tab) =>
                                segButton(
                                    segmentType === tab.type,
                                    tab.label,
                                    () => setSegmentTypeSafe(tab.type),
                                    tab.type
                                )
                            )}
                        </div>

                        {segmentType === "rfm" ? (
                            <select
                                className="ae-input"
                                aria-label="RFM segment"
                                value={rfmValue}
                                onChange={(event) => {
                                    setRfmValue(event.target.value);
                                    invalidate();
                                }}
                            >
                                <option value="">Select a segment…</option>
                                {rfmSegments.map((segment) => (
                                    <option key={segment} value={segment}>
                                        {segment}
                                    </option>
                                ))}
                            </select>
                        ) : null}

                        {segmentType === "lifecycle" ? (
                            <select
                                className="ae-input"
                                aria-label="Lifecycle stage"
                                value={lifecycleValue}
                                onChange={(event) => {
                                    setLifecycleValue(event.target.value);
                                    invalidate();
                                }}
                            >
                                {LIFECYCLE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        ) : null}
                    </div>

                    <div className="ae-field">
                        <span className="ae-field__lbl">File name</span>
                        <input
                            className="ae-input ae-input--file"
                            aria-label="File name"
                            value={fileName}
                            onChange={(event) => {
                                setFileName(event.target.value);
                                setFileNameEdited(true);
                            }}
                        />
                    </div>

                    <div className="ae-actionrow">
                        {loading ? (
                            <div className="aeC" aria-live="polite">
                                <div className="aeC__statusline">
                                    <span className="aeC__icon">
                                        <span aria-hidden className="material-symbols-outlined">
                                            groups
                                        </span>
                                    </span>
                                    <span>
                                        <div className="aeC__status">Generating hashed audience</div>
                                        <div className="aeC__sub">
                                            {scopeLabel} · {segmentLabel} · SHA-256
                                        </div>
                                    </span>
                                    <span className="aeC__pct">{progress}%</span>
                                </div>
                                <div className="aeC__track">
                                    <div className="aeC__fill" style={{ width: `${progress}%` }} />
                                </div>
                            </div>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="ae-btn"
                                    disabled={generateDisabled}
                                    onClick={() =>
                                        onExport(
                                            { ...baseFilters, clients: effectiveWorkspaces },
                                            { type: segmentType, value: segmentValue }
                                        )
                                    }
                                >
                                    <span aria-hidden className="material-symbols-outlined">
                                        bolt
                                    </span>
                                    {ready ? "Regenerate" : "Generate audience"}
                                </button>
                                {error !== null ? (
                                    <span className="ae-error" role="alert">
                                        {error}
                                    </span>
                                ) : null}
                            </>
                        )}
                    </div>

                    {!loading && ready ? (
                        <div className="ae-done" role="status" style={{ marginTop: 14 }}>
                            <span className="ae-done__check">
                                <CheckMark />
                            </span>
                            <div className="ae-done__body">
                                <div className="ae-done__title">
                                    Audience ready
                                    {count !== null ? ` · ${count.toLocaleString()} accounts` : ""}
                                </div>
                                <div className="ae-done__sub">
                                    <span className="file">{fileName}</span> · ready to download
                                </div>
                            </div>
                            <button
                                type="button"
                                className="ae-btn ae-btn--sm"
                                disabled={fileName.trim() === ""}
                                onClick={() => onSave(fileName.trim())}
                            >
                                <span aria-hidden className="material-symbols-outlined">
                                    download
                                </span>
                                Download CSV
                            </button>
                        </div>
                    ) : null}

                    <p className="ae-hint">
                        Email, name and country are <b>SHA-256 hashed</b> before download — upload the
                        file straight to Meta. One row per account; onramp and Apple-relay addresses
                        are excluded.
                    </p>
                </div>
            </div>
        </section>
    );
};
